import React, { FC } from "react";
import { Box } from "@mui/material";
import { Img } from "../Img";
import { formatImgUrl } from "utils/helpers";
import { PLAN_NAMES } from "constants/data";
import useMediaBreakpoints from "hooks/useMediaBreakpoints";
import { StyledRouterA } from "../Text";

interface GalleryDisplayProps {
  medias: string[];
  destinationPath: string;
  maxHeight?: number;
  planName?: string;
  isProduct?: boolean;
  productPath?: string;
  fullRadius?: boolean;
}

interface GalleryImageProps {
  src: string;
  alt: string;
  borderRadius?: {
    topLeft?: number;
    topRight?: number;
    bottomLeft?: number;
    bottomRight?: number;
  };
}

// Reusable Gallery Image component
const GalleryImage: FC<GalleryImageProps> = ({
  src,
  alt,
  borderRadius = {},
}) => {
  const { topLeft, topRight, bottomLeft, bottomRight } = borderRadius;

  return (
    <Img
      src={formatImgUrl(src, alt === "main-media" ? 4 : undefined)}
      alt={alt}
      fillContainer
      style={{
        objectFit: "cover",
        width: "100%",
        height: "100%",
        ...(topLeft && { borderTopLeftRadius: topLeft }),
        ...(topRight && { borderTopRightRadius: topRight }),
        ...(bottomLeft && { borderBottomLeftRadius: bottomLeft }),
        ...(bottomRight && { borderBottomRightRadius: bottomRight }),
      }}
    />
  );
};

// Mobile three-image layout (1 big + 2 stacked)
const MobileThreeImageLayout: FC<{
  displayMedias: string[];
  maxHeight: number;
  isMobile: boolean;
  fullRadius?: boolean;
}> = ({ displayMedias, maxHeight, isMobile, fullRadius }) => {
  // Configure border radius based on device and fullRadius prop
  const leftBorderRadius = fullRadius
    ? { topLeft: 8, bottomLeft: 8 }
    : isMobile
    ? { topLeft: 8, bottomLeft: 0 }
    : { topLeft: 8, bottomLeft: 8 };

  const topRightBorderRadius = fullRadius
    ? { topRight: 8 }
    : isMobile
    ? { topRight: 8 }
    : { topRight: 0 };

  const bottomRightBorderRadius = fullRadius
    ? { bottomRight: 8 }
    : { bottomRight: 0 };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        maxHeight: `${maxHeight}px`,
        gap: "2px",
        overflow: "hidden",
      }}
    >
      {/* Left: big image */}
      <Box
        sx={{
          width: "66.66%",
          height: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <GalleryImage
          src={displayMedias[0]}
          alt="main-media"
          borderRadius={leftBorderRadius}
        />
      </Box>

      {/* Right: 2 stacked images */}
      <Box
        sx={{
          width: "33.33%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        {[displayMedias[1], displayMedias[2]].map((media, idx) => (
          <Box
            key={idx}
            sx={{
              flex: 1,
              width: "100%",
              height: "50%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <GalleryImage
              src={media}
              alt={`media-${idx + 2}`}
              borderRadius={{
                ...(idx === 0 && topRightBorderRadius),
                ...(idx === 1 && bottomRightBorderRadius),
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Single image layout
const SingleImageLayout: FC<{
  image: string;
  maxHeight: number;
  isMobile: boolean;
  fullRadius?: boolean;
}> = ({ image, maxHeight, isMobile, fullRadius }) => {
  // Configure border radius based on device and fullRadius prop
  const borderRadiusConfig = fullRadius
    ? { topLeft: 8, topRight: 8, bottomLeft: 8, bottomRight: 8 }
    : isMobile
    ? { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 }
    : { topLeft: 8, topRight: 0, bottomLeft: 8, bottomRight: 0 };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        maxHeight: `${maxHeight}px`,
        overflow: "hidden",
        position: "relative",
      }}
    >
      <GalleryImage
        src={image}
        alt="main-media"
        borderRadius={borderRadiusConfig}
      />
    </Box>
  );
};

// Multiple images standard layout
const StandardMultipleImageLayout: FC<{
  displayMedias: string[];
  maxHeight: number;
  isMobile: boolean;
  fullRadius?: boolean;
}> = ({ displayMedias, maxHeight, isMobile, fullRadius }) => {
  // Configure border radius based on device and fullRadius prop
  const topImageBorderRadius = fullRadius
    ? { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 }
    : isMobile
    ? { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 }
    : { topLeft: 8, topRight: 0, bottomLeft: 0, bottomRight: 0 };

  const bottomLeftBorderRadius = fullRadius
    ? { bottomLeft: 8 }
    : isMobile
    ? { bottomLeft: 0 }
    : { bottomLeft: 8 };

  const bottomRightBorderRadius = fullRadius
    ? { bottomRight: 8 }
    : { bottomRight: 0 };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        maxHeight: `${maxHeight}px`,
        flexDirection: "column",
        gap: "2px",
        overflow: "hidden",
        flex: 1,
      }}
    >
      {/* Top image - takes 2/3 height */}
      <Box
        sx={{
          height: "66.66%",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          flex: "2 0 auto",
        }}
      >
        {displayMedias.length > 0 && (
          <GalleryImage
            src={displayMedias[0]}
            alt="main-media"
            borderRadius={topImageBorderRadius}
          />
        )}
      </Box>

      {/* Bottom row with remaining images */}
      <Box
        sx={{
          height: "33.33%",
          width: "100%",
          display: "flex",
          gap: "2px",
          flex: "1 0 auto",
        }}
      >
        {displayMedias.slice(1).map((media, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <GalleryImage
              src={media}
              alt={`media-${i + 1}`}
              borderRadius={{
                ...(i === 0 && bottomLeftBorderRadius),
                ...(i === displayMedias.slice(1).length - 1 &&
                  bottomRightBorderRadius),
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Main component
const GalleryDisplay: FC<GalleryDisplayProps> = ({
  medias,
  destinationPath,
  maxHeight,
  planName,
  isProduct = false,
  productPath,
  fullRadius = false,
}) => {
  const { isMobile } = useMediaBreakpoints();

  // Determine number of images to show based on plan
  const maxImages =
    planName === PLAN_NAMES.advance
      ? 4
      : planName === PLAN_NAMES.starter
      ? 3
      : 1;
  const displayMedias = medias.slice(0, maxImages);

  // Calculate maxHeight based on parameters and conditions
  const computedMaxHeight = calculateMaxHeight(
    maxHeight,
    planName,
    isMobile,
    displayMedias.length
  );

  // Special mobile starter layout
  const isMobileStarter =
    isMobile && planName !== PLAN_NAMES.advance && displayMedias.length === 3;

  // Determine link for main image
  const mainImageLink =
    isProduct && productPath ? productPath : destinationPath;

  // Configure border radius based on device and fullRadius prop
  const containerBorderRadius = fullRadius
    ? {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
      }
    : isMobile
    ? {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
      }
    : {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
      };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        padding: 0,
        overflow: "hidden",
        ...containerBorderRadius,
      }}
    >
      <StyledRouterA
        to={mainImageLink}
        style={{
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center",
          overflow: "hidden",
          height: "100%",
          width: "100%",
          textDecoration: "none",
          padding: 0,
          ...containerBorderRadius,
        }}
      >
        {isMobileStarter ? (
          <MobileThreeImageLayout
            displayMedias={displayMedias}
            maxHeight={computedMaxHeight}
            isMobile={isMobile}
            fullRadius={fullRadius}
          />
        ) : displayMedias.length === 1 ? (
          <SingleImageLayout
            image={displayMedias[0]}
            maxHeight={computedMaxHeight}
            isMobile={isMobile}
            fullRadius={fullRadius}
          />
        ) : (
          <StandardMultipleImageLayout
            displayMedias={displayMedias}
            maxHeight={computedMaxHeight}
            isMobile={isMobile}
            fullRadius={fullRadius}
          />
        )}
      </StyledRouterA>
    </Box>
  );
};

// Helper function to calculate maxHeight
function calculateMaxHeight(
  maxHeight: number | undefined,
  planName: string | undefined,
  isMobile: boolean,
  mediaCount: number
): number {
  if (maxHeight) return maxHeight;

  if (isMobile && planName !== PLAN_NAMES.advance && mediaCount === 3) {
    return 160; // Starter mobile 3 ảnh: nhỏ hơn Superior
  }

  const isFreePlan =
    planName !== PLAN_NAMES.advance && planName !== PLAN_NAMES.starter;

  // For desktop single image layout
  if (!isMobile && mediaCount === 1) {
    if (isFreePlan) {
      return 200; // Free plan with single image
    }
    return 310; // Other plans with single image
  }

  if (planName === PLAN_NAMES.advance || planName === PLAN_NAMES.starter) {
    return 250; // Default height for Advance and Starter plans
  } else {
    return 200; // 50px smaller for other plans
  }
}

export default GalleryDisplay;
